import React from 'react';
import { Pickaxe } from 'lucide-react';
import { explainerSlugKey } from 'src/shared/constants/portfolio';
import { PortfolioData } from 'src/app/portfolio/state/api/portfolioQuery';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import { SortOrder } from 'src/shared/types';
import { HabitatInsightsMetricType } from 'src/shared/types/graph/survey';
import PortfolioExploreButton from './PortfolioExploreButton';
import PortfolioGaugeChart from './PortfolioGaugeChart';
import SummaryCard from './SummaryCard';

const HumanModificationChart = ({ portfolioData }: { portfolioData: PortfolioData }) => {
    const humanModification = portfolioData.summary.metrics.find(
        metric => metric.name === HabitatInsightsMetricType.HUMAN_MODIFICATION_INDEX
    );
    const navigate = useAppNavigation();
    const { currentCustomerId } = useCustomer();

    if (!humanModification || !humanModification.data || !humanModification.year) {
        return null;
    }

    let modificationCategory = 'No human modification';
    let modificationColor = 'green';
    if (humanModification.data > 0.01 && humanModification.data <= 0.1) {
        modificationCategory = 'Low modification';
    } else if (humanModification.data > 0.1 && humanModification.data <= 0.4) {
        modificationCategory = 'Moderate modification';
        modificationColor = 'orange';
    } else if (humanModification.data > 0.4 && humanModification.data <= 0.7) {
        modificationCategory = 'High modification';
        modificationColor = 'red';
    } else if (humanModification.data > 0.7) {
        modificationCategory = 'Very high modification';
        modificationColor = 'red';
    }

    const chartData = {
        chartValue: humanModification.data,
        startLabel: 'Natural',
        endLabel: (
            <>
                Very high
                <br />
                modification
            </>
        ),
    };

    const hmiSummary = portfolioData.summary.hmiSiteSummary;

    // Find the "next worst" category that has sites
    let worstCategorySites = 0;
    let worstCategoryLabel = '';

    if (hmiSummary.very_high_modification > 0) {
        worstCategorySites = hmiSummary.very_high_modification;
        worstCategoryLabel = 'very high modification';
    } else if (hmiSummary.high_modification > 0) {
        worstCategorySites = hmiSummary.high_modification;
        worstCategoryLabel = 'high modification';
    } else if (hmiSummary.medium_modification > 0) {
        worstCategorySites = hmiSummary.medium_modification;
        worstCategoryLabel = 'medium modification';
    } else if (hmiSummary.low_modification > 0) {
        worstCategorySites = hmiSummary.low_modification;
        worstCategoryLabel = 'low modification';
    } else if (hmiSummary.no_modification > 0) {
        worstCategorySites = hmiSummary.no_modification;
        worstCategoryLabel = 'no modification';
    }

    const handleExploreClick = () => {
        navigate.toPortfolioSiteExplorer({
            customerId: currentCustomerId,
            selectedMetric: HabitatInsightsMetricType.HUMAN_MODIFICATION_INDEX,
            sortDirection: SortOrder.DESC,
            view: 'list',
        });
    };

    return (
        <SummaryCard
            dataTestId='portfolio-human-modification-chart'
            containerClasses='gap-3 p-5'
            cardIcon={<Pickaxe className='text-primary' />}
            cardTitle={
                <span className='flex flex-col items-start'>
                    Human Modification Index
                    <span className='text-sm text-grey-80'>For {humanModification.year}</span>
                </span>
            }
            cardSubtitle='Quantifies the extent of human alteration to natural landscapes.'
            titleExplainerSlug={{
                defaultSlug: explainerSlugKey.portfolioHumanModificationIndex,
                explainerSlug: `${explainerSlugKey.portfolioHumanModificationIndex}-te`,
            }}
            cardBody={
                <PortfolioGaugeChart
                    sites={worstCategorySites}
                    totalSites={portfolioData.summary.numberSites}
                    chartData={chartData}
                    chartColor={modificationColor}
                    category={modificationCategory}
                    summaryText={
                        <div className='mb-2'>
                            <span className='text-sm text-grey-80'>of your sites have </span>
                            <span className='text-sm text-grey-80 font-bold'>{worstCategoryLabel}</span>
                        </div>
                    }
                />
            }
            action={<PortfolioExploreButton sectionName='human-modification' onClick={handleExploreClick} />}
            frameworks={humanModification.frameworks}
        />
    );
};

export default HumanModificationChart;
